import React, { useEffect, useState, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from './components/Footer';
import Navbar from './components/Navbar';
import Toast from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingPage from './components/LoadingPage';
import StructuredData from './components/StructuredData';
import { setUserProperties } from './src/lib/analytics';
import { trackPageview } from './src/lib/umami';
import { CitiesProvider } from './src/contexts/CitiesContext';

// 页面组件
import HomePage from './pages/HomePage';
import JobListPage from './pages/JobListPage';
import JobDetailPage from './pages/JobDetailPage';
import CityDetailPage from './pages/CityDetailPage';
import OfficesPage from './pages/OfficesPage';
import LifePage from './pages/LifePage';
import GalleryPage from './pages/GalleryPage';
import AlbumDetailPage from './pages/AlbumDetailPage';
import NotFoundPage from './pages/NotFoundPage';

const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isRouteChanging, setIsRouteChanging] = useState(false);

  // 设置用户属性
  useEffect(() => {
    setUserProperties({
      preferred_language: i18n.language,
      referrer: document.referrer || 'direct',
    });
  }, [i18n.language]);

  // SPA 路由变化时手动发送 pageview（首次加载由 umami 自动处理）
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    trackPageview();
  }, [location.pathname]);

  // Handle route change loading state
  useEffect(() => {
    setIsRouteChanging(true);
    const timer = setTimeout(() => setIsRouteChanging(false), 300);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Update HTML metadata when language changes
  useEffect(() => {
    const baseUrl = window.location.origin;

    // 1. Update HTML lang attribute
    document.documentElement.lang = i18n.language;

    // 2. Update page title (基础标题，各页面会覆盖)
    document.title = t('html.title');

    // 3. Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', t('html.description'));
    }

    // 4. Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = `${baseUrl}${location.pathname}`;

    // 5. Update hreflang tags
    const updateOrCreateHreflang = (lang: string, href: string) => {
      let link = document.querySelector(`link[hreflang="${lang}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lang);
        document.head.appendChild(link);
      }
      link.href = href;
    };

    updateOrCreateHreflang('en', `${baseUrl}/en${location.pathname}`);
    updateOrCreateHreflang('zh', `${baseUrl}/zh${location.pathname}`);
    updateOrCreateHreflang('x-default', `${baseUrl}${location.pathname}`);

    // 6. Update Open Graph tags
    const updateOgTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    updateOgTag('og:title', t('html.title'));
    updateOgTag('og:description', t('html.description'));
    updateOgTag('og:locale', i18n.language === 'zh' ? 'zh_CN' : 'en_US');
    updateOgTag('og:url', `${baseUrl}${location.pathname}`);

    // 7. Update Twitter Card tags
    const updateTwitterTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="twitter:${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', `twitter:${name}`);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    updateTwitterTag('card', 'summary_large_image');
    updateTwitterTag('title', t('html.title'));
    updateTwitterTag('description', t('html.description'));

  }, [t, i18n.language, location.pathname]);

  // Smooth scroll to top when route changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  return (
    <ErrorBoundary>
      <CitiesProvider>
      <div className="min-h-screen flex flex-col selection:bg-blue-100 selection:text-blue-900">
        <Navbar />

        <main className="flex-grow relative">
          {/* Route change loading overlay */}
          {isRouteChanging && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center">
              <LoadingPage fullScreen={false} message="" />
            </div>
          )}

          <Suspense fallback={<LoadingPage fullScreen={false} />}>
            <Routes>
              <Route path="/" element={
                <>
                  <StructuredData type="organization" />
                  <HomePage />
                </>
              } />
              <Route path="/jobs" element={<JobListPage />} />
              <Route path="/jobs/:jobId" element={<JobDetailPage />} />
              <Route path="/cities/:cityId" element={<CityDetailPage />} />
              <Route path="/offices" element={<OfficesPage />} />
              <Route path="/life" element={<LifePage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/gallery/:albumId" element={<AlbumDetailPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </main>

        <Footer />
        <Toast />
      </div>
      </CitiesProvider>
    </ErrorBoundary>
  );
};

export default App;
